import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type {
  IGetFavouritesParams,
  IReportGroup,
  IReportsListParams,
  IToggleFavouriteParams,
} from "./Types/ReportsServiceTypes";
import { EndpointService } from "@/types/api/api";
import type { ReportSchema } from "@dinesapp/dines-ui/modules/reporting/types/report";

export interface IReportsService {
  list: (
    params: IReportsListParams,
  ) => UseQueryResult<Array<IReportGroup>, unknown>;

  get: (
    id: string,
    params: Record<string, unknown>,
  ) => UseQueryResult<ReportSchema, unknown>;

  getFavourites: (
    params: IGetFavouritesParams,
  ) => UseQueryResult<Array<IReportGroup>, unknown>;
  toggleFavourite: (
    listParams: IReportsListParams,
  ) => UseMutationResult<void, unknown, IToggleFavouriteParams>;
}

export class ReportsService implements IReportsService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params: IReportsListParams,
  ): UseQueryResult<Array<IReportGroup>, unknown> {
    const updatedParams: IReportsListParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["reports", "list"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { isOrganisation, ...restParams } = updatedParams;

        const { data } = await this.api.get<AxiosResponse<Array<IReportGroup>>>(
          "/orgreports",
          {
            params: restParams,
            endpointService: EndpointService.Reports,
          },
        );

        return data;
      },
    });
  }

  get(
    id: string,
    params: Record<string, unknown>,
  ): UseQueryResult<ReportSchema, unknown> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["reports", "get"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<ReportSchema>>(
          `/orgreports/get_reports/${id}`,
          {
            params: {
              ...updatedParams,
            },
            arrayFormat: "indices",
            endpointService: EndpointService.Reports,
          },
        );

        return data;
      },
      retry: false, // Disable retry to immediately handle errors
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true, // always refetch recent data
      refetchOnWindowFocus: true, // same here
    });
  }

  getFavourites(
    params: IGetFavouritesParams,
  ): UseQueryResult<Array<IReportGroup>, unknown> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["reports", "favourites", "list"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<Array<IReportGroup>>>(
          "/orgreports/",
          {
            params: {
              favourited: true,
              ...updatedParams,
            },
            endpointService: EndpointService.Reports,
            arrayFormat: "indices",
          },
        );

        return data;
      },
    });
  }

  toggleFavourite(
    listParams: IReportsListParams,
  ): UseMutationResult<void, unknown, IToggleFavouriteParams> {
    const queryClient = useQueryClient();
    const buildQueryKeyParams = () => {
      return Object.fromEntries(
        Object.entries(listParams).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
    };
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["reports", "favourites", "toggle"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params: IToggleFavouriteParams) => {
        const { id, isFavourited } = params;
        const reportURL = isFavourited
          ? "/orgreports/favourite"
          : `/orgreports/unfavourite/${id}`;

        const requestMethod = isFavourited ? "post" : "delete";

        const updatedParams = isFavourited
          ? {
              report: id,
            }
          : {};

        const { data } = await this.api[requestMethod]<AxiosResponse<void>>(
          reportURL,
          {
            endpointService: EndpointService.Reports,
            params: updatedParams,
          },
        );

        return data;
      },
      onMutate: async (params) => {
        // Cancel any ongoing queries to prevent them from updating the cache
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["reports", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["reports", "favourites", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
        });
        // Store the previous state of the cache to rollback in case of an error
        const previousReports = queryClient.getQueryData<IReportGroup[]>(
          buildQueryKey({
            queryKey: ["reports", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
        );

        const previousFavourites = queryClient.getQueryData<IReportGroup[]>(
          buildQueryKey({
            queryKey: ["reports", "favourites", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
        );

        // Optimistically update the cache for the report being toggled
        queryClient.setQueryData(
          buildQueryKey({
            queryKey: ["reports", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
          (old: Array<IReportGroup>) => {
            return old.map((group) => ({
              ...group,
              categories: group.categories.map((category) => ({
                ...category,
                reports: category.reports.map((report) =>
                  report.id === params.id
                    ? {
                        ...report,
                        favourited: params.isFavourited,
                        isUpdating: true,
                      }
                    : report,
                ),
              })),
            }));
          },
        );

        // Optimistically update the cache for the favourite reports
        queryClient.setQueryData(
          buildQueryKey({
            queryKey: ["reports", "favourites", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
          (old: Array<IReportGroup>) => {
            return old.map((group) => ({
              ...group,
              categories: group.categories
                .map((category) => ({
                  ...category,
                  reports: category.reports
                    .map((report) =>
                      report.id === params.id
                        ? {
                            ...report,
                            favourited: params.isFavourited,
                            isUpdating: true,
                          }
                        : report,
                    )
                    .filter((report) => report.favourited || report.isUpdating),
                }))
                .filter((category) => category.reports.length > 0),
            }));
          },
        );
        // Return the previous state of the cache to rollback in case of an error
        return { previousReports, previousFavourites };
      },
      onError: (err, params, context) => {
        // Rollback the cache to the previous state
        queryClient.setQueryData(
          buildQueryKey({
            queryKey: ["reports", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
          context.previousReports,
        );
        queryClient.setQueryData(
          buildQueryKey({
            queryKey: ["reports", "favourites", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
          context.previousFavourites,
        );
      },
      onSettled: () => {
        // Invalidate the queries to refetch the data
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["reports", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
        });
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["reports", "favourites", "list"],
            venueID: this.api.selectedVenue,
            params: {
              ...buildQueryKeyParams(),
            },
          }),
        });
      },
    });
  }
}
